import React, { useEffect, useState, useMemo } from 'react';
import Layout from '../../components/layout/AppLayout';
import { useSidebar } from '../../../shared/contexts/SidebarContext';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  getAvailability,
  acceptRejectLeave,
  getFollowUpsByProjectId,
  getSalesDashboardShell,
  getSalesActivityMetrics,
  getAllActiveSalesUsersByProjectId,
  downloadInvoice,
  getLeadCardsStatsPerUser,
  getSalesDashboardStatsByProjectId
} from '../../services/api/implementation/impl';
import AiPulsePanel from '../../components/ai/AiPulsePanel';
import SalesUserActivityMetrics from './SalesUserActivityMetrics';
import { useLocation, useNavigate } from 'react-router-dom';
import Button from '../../../shared/components/ui/Button';
import Modal from '../../../shared/components/ui/Modal';
import toast from 'react-hot-toast';
import { axiosErrorMessage, isEmptyPdfPayload, triggerPdfDownload } from '../../utils/downloadPdf';
import Cookies from 'js-cookie';
import mixpanel from 'mixpanel-browser';
import Loader from '../../../shared/components/ui/Loader';
import Header from '../../../shared/components/layout/Header';
import SalesGraphs from './SalesGraphs';
import DashboardFilters from '../../../shared/components/ui/DashboardFilters';
import MetricCard from '../../components/ui/MetricCard';
import {
  AlertTriangle,
  CalendarCheck2,
  CircleCheck,
  CircleX,
  Clock3,
  HandCoins,
  Handshake,
  Home,
  IndianRupee,
  Loader2,
  PauseCircle,
  CalendarClock,
  UserPlus,
  Users,
  Wallet
} from 'lucide-react';
import { buildReturnToFromLocation, navigateWithReturn, useSmartBack, shouldShowModuleHomeBack } from '../../../shared/navigation';
import {
  buildPriorityLeadsSearch,
  buildSalesDashboardFilterParams,
  getSalesDashboardFiltersFromSearch
} from './salesUrlParams';

const ACTIVITY_DATE_PRESETS = [
  { value: 'today', label: 'Today' },
  { value: 'mtd', label: 'Month till date' },
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'this_quarter', label: 'This quarter' },
  { value: 'custom', label: 'Custom' }
];

const SalesDashboard = ({
  skipLayout = false,
  skipHeader = false,
  skipFilters = false,
  projectID: externalProjectID = null,
  month: externalMonth = null,
  onFiltersChange: externalOnFiltersChange = null
}) => {
  const { collapsed } = useSidebar();
  const [selectedActivity, setSelectedActivity] = useState();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { onBack } = useSmartBack({ fallback: '/builder/dashboard' });
  const showHomeBack = !skipHeader && shouldShowModuleHomeBack('sales', location);
  const urlFilters = useMemo(
    () => getSalesDashboardFiltersFromSearch(new URLSearchParams(location.search)),
    [location.search]
  );
  const [filters, setFilters] = useState({
    projectID: externalProjectID || urlFilters.projectID || null,
    month: externalMonth || urlFilters.month || null
  });
  const [isFiltersLoading, setIsFiltersLoading] = useState(false);
  const userID = Number(Cookies.get('userId'));
  const [designation, setDesignation] = useState(
    () => Cookies.get('userDesignationLevel') || ''
  );
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState(null);
  const [salesActionCenterTab, setSalesActionCenterTab] = useState('priorities');
  const [activitySalesUserId, setActivitySalesUserId] = useState('');
  const [activityDatePreset, setActivityDatePreset] = useState('today');
  const [activityCustomFrom, setActivityCustomFrom] = useState('');
  const [activityCustomTo, setActivityCustomTo] = useState('');

  const syncDashboardFiltersToUrl = (nextFilters) => {
    if (externalOnFiltersChange) return;
    const params = buildSalesDashboardFilterParams(
      new URLSearchParams(location.search),
      nextFilters
    );
    const qs = params.toString();
    navigate(
      {
        pathname: location.pathname,
        search: qs ? `?${qs}` : ''
      },
      { replace: true }
    );
  };

  const goToPriorityLeads = (state) => {
    const params = buildPriorityLeadsSearch(state);
    const qs = params.toString();
    navigateWithReturn(navigate, `/builder/priority/leads${qs ? `?${qs}` : ''}`, {
      returnTo: buildReturnToFromLocation(location),
      state
    });
  };

  useEffect(() => {
    setDesignation(Cookies.get('userDesignationLevel') || '');
  }, []);

  useEffect(() => {
    if (designation === 'Low' && salesActionCenterTab === 'approvals') {
      setSalesActionCenterTab('priorities');
    }
  }, [designation, salesActionCenterTab]);

  const { data: availability, refetch: refetAvailability } = useQuery({
    queryKey: ['allAvailability', filters.projectID],
    queryFn: () => getAvailability(filters.projectID),
    enabled: !!filters.projectID
  });

  const {
    data: followUps,
    refetch: refetchFollowUps
  } = useQuery({
    queryKey: ['followUps', filters.projectID, 'dashboard', userID, designation],
    queryFn: async () => {
      const designationLevel =
        designation || Cookies.get('userDesignationLevel') || '';
      const loadScopedFollowUps = () =>
        getFollowUpsByProjectId(filters.projectID, null, {
          limit: 200,
          status: 'Active',
          // Match mobile: pin Low sales users to their own assigned follow-ups.
          // Medium/Management keep BE ACL (team / company) without an extra filter.
          ...(designationLevel === 'Low' && userID ? { sales_id: userID } : {})
        });

      try {
        const shell = await getSalesDashboardShell(filters.projectID);
        const shellRows = shell?.data?.followUps;
        const summary = shell?.data?.summary;
        if (Array.isArray(shellRows)) {
          const assigneeScoped = Boolean(summary?.assigneeScoped);
          const truncated = Boolean(summary?.truncated);
          const mineInShell = shellRows.some(
            (item) => Number(item?.user?.id ?? item?.assignedto) === userID
          );

          // New BE scopes Sales shell rows. Old BE (or a truncated unscoped
          // shell with none of "mine") must fall back so Priorities aren't empty
          // while mobile still shows Today/Delayed cards.
          if (!assigneeScoped && userID && (truncated || !mineInShell)) {
            const scoped = await loadScopedFollowUps();
            const scopedRows = Array.isArray(scoped?.data) ? scoped.data : [];
            const shellOthers = shellRows.filter(
              (item) => Number(item?.user?.id ?? item?.assignedto) !== userID
            );
            const byId = new Map();
            [...scopedRows, ...shellOthers].forEach((row) => {
              if (row?.id != null) byId.set(row.id, row);
            });
            return {
              data: Array.from(byId.values()),
              meta: {
                ...summary,
                activeCount:
                  Number(scoped?.meta?.total) >= 0
                    ? Number(scoped.meta.total)
                    : summary?.activeCount,
                sourcedFrom: 'scoped-fallback'
              }
            };
          }

          return { data: shellRows, meta: summary };
        }
      } catch {
        // fall through to paginated follow-ups
      }
      return loadScopedFollowUps();
    },
    enabled: !!filters.projectID,
    staleTime: 60_000,
    onSuccess: (data) => {
      mixpanel.track('Follow Ups API Success', {
        user_id: userID,
        project_id: filters.projectID,
        follow_ups_count: data?.data?.length || 0,
        timestamp: new Date().toISOString()
      });
    },
    onError: (error) => {
      mixpanel.track('Follow Ups API Failed', {
        user_id: userID,
        project_id: filters.projectID,
        error_message: error?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  });

  const {
    data: salesStatsCards,
    isLoading: isLoadingSalesStats,
    isFetching: isFetchingSalesStats
  } = useQuery({
    queryKey: ['salesDashboardStatsCards', filters.projectID],
    queryFn: () => getSalesDashboardStatsByProjectId(filters.projectID),
    enabled: !!filters.projectID
  });

  const { data: leadPipelineStats } = useQuery({
    queryKey: ['leadStatsCards', filters.projectID],
    queryFn: () => getLeadCardsStatsPerUser(filters.projectID),
    enabled: !!filters.projectID
  });

  const activityDateParams = useMemo(() => {
    if (activityDatePreset === 'custom') {
      if (!activityCustomFrom || !activityCustomTo) {
        return { preset: 'today' };
      }
      return {
        preset: 'custom',
        from: activityCustomFrom,
        to: activityCustomTo
      };
    }
    return { preset: activityDatePreset };
  }, [activityDatePreset, activityCustomFrom, activityCustomTo]);

  const activityQuerySalesUserId =
    designation === 'Low' ? userID : activitySalesUserId || undefined;

  const { data: projectSalesUsersData } = useQuery({
    queryKey: ['projectActiveSalesUsers', filters.projectID],
    queryFn: () => getAllActiveSalesUsersByProjectId(filters.projectID, 1, 500),
    enabled: !!filters.projectID && salesActionCenterTab === 'activity' && designation !== 'Low',
    staleTime: 5 * 60_000
  });

  const projectSalesUsers = useMemo(() => {
    const rows = Array.isArray(projectSalesUsersData?.data)
      ? projectSalesUsersData.data
      : Array.isArray(projectSalesUsersData)
        ? projectSalesUsersData
        : [];
    return rows
      .map((u) => ({
        userId: u.id ?? u.userId ?? u.user_id,
        name: u.name || u.full_name || `User ${u.id}`
      }))
      .filter((u) => u.userId != null)
      .sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [projectSalesUsersData]);

  const {
    data: activityMetricsData,
    isLoading: isLoadingActivityMetrics,
    isFetching: isFetchingActivityMetrics,
    dataUpdatedAt: activityMetricsUpdatedAt,
  } = useQuery({
    queryKey: [
      'salesActivityMetrics',
      filters.projectID,
      designation === 'Low' ? userID : activitySalesUserId || 'all',
      activityDateParams.preset,
      activityDateParams.from || '',
      activityDateParams.to || ''
    ],
    queryFn: () =>
      getSalesActivityMetrics(filters.projectID, {
        salesUserId: activityQuerySalesUserId,
        ...activityDateParams
      }),
    enabled: !!filters.projectID && salesActionCenterTab === 'activity',
    staleTime: 60_000,
    refetchInterval: 5 * 60 * 1000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    placeholderData: (previousData) => previousData
  });

  const activityFilterOptions = projectSalesUsers;
  const activityMetrics = activityMetricsData?.metrics || [];
  const activitySyncedLabel = useMemo(() => {
    const iso =
      activityMetricsData?.syncedAt ||
      (activityMetricsUpdatedAt
        ? new Date(activityMetricsUpdatedAt).toISOString()
        : null);
    if (!iso) return null;
    const at = new Date(iso);
    if (Number.isNaN(at.getTime())) return null;
    return at.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Asia/Kolkata'
    });
  }, [activityMetricsData?.syncedAt, activityMetricsUpdatedAt]);
  const isSalesStatsLoading = !!filters.projectID && isLoadingSalesStats;

  const handleFiltersChange = ({ projectID, month }) => {
    const next = { projectID, month };
    setFilters(next);
    syncDashboardFiltersToUrl(next);
    // Also notify parent if external handler is provided
    if (externalOnFiltersChange) {
      externalOnFiltersChange({ projectID, month });
    }
  };

  const handleFiltersLoadingChange = (isLoading) => {
    setIsFiltersLoading(isLoading);
  };

  // Update filters when external props change
  useEffect(() => {
    if (externalProjectID !== null || externalMonth !== null) {
      setFilters({
        projectID: externalProjectID || filters.projectID,
        month: externalMonth || filters.month
      });
    }
  }, [externalProjectID, externalMonth]);

  const AvatarCircle = ({ name, variant = 'priority' }) => {
    const bg =
      variant === 'escalation'
        ? 'bg-red-500'
        : variant === 'absent'
          ? 'bg-orange-500'
          : variant === 'approval'
            ? 'bg-purple-500'
            : 'bg-blue-500';
    return (
      <div
        className={`w-6 h-6 shrink-0 rounded-full ${bg} text-white flex items-center justify-center text-xs font-semibold`}>
        {name?.charAt(0).toUpperCase()}
      </div>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short'
    });
  };

  // Helper function to group and sort data
  const processAvailabilityData = (data) => {
    if (!data) return [];

    // Sort by date
    const sorted = [...data].sort((a, b) => new Date(a.date) - new Date(b.date));

    // Group by formatted date
    const grouped = sorted.reduce((groups, item) => {
      const dateKey = formatDate(item.date);
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(item);
      return groups;
    }, {});

    return Object.entries(grouped);
  };

  // Pre-filter all pending approval items so only those with status not true are used
  const filteredAvailabilityData = useMemo(() => {
    if (!availability?.data) return [];
    return processAvailabilityData(availability.data)
      .map(([date, items]) => [date, items.filter((item) => !item.status)])
      .filter(([, filteredItems]) => filteredItems.length > 0);
  }, [availability?.data]);

  const processFollowUpsData = (data) => {
    if (!data || !Array.isArray(data)) return [];

    let currentUserName = '';

    // First find current user's name from the data
    const currentUserItem = data.find((item) => item.user?.id === userID);
    if (currentUserItem) {
      currentUserName = currentUserItem.user.name;
    }

    // Categorize follow-ups
    const categorized = {
      myFollowUps: {
        byDate: {} // key will be date string (e.g., "24 Apr")
      },
      otherSales: {} // key will be user ID
    };

    // Helper function to format date as "DD MMM"
    const formatDate = (dateString) => {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
    };

    data.forEach((item) => {
      // Skip completed items
      if (item.status === 'Inactive') return;

      const isMine = Number(item.user?.id ?? item.assignedto) === userID;

      if (isMine) {
        // For my follow-ups, group by date
        const followUpDate = item.nextFollowUpDate ? formatDate(item.nextFollowUpDate) : 'No Date';

        if (!categorized.myFollowUps.byDate[followUpDate]) {
          categorized.myFollowUps.byDate[followUpDate] = {
            default: [],
            payment: [],
            possession: [],
            closeddeal: [],
            underConsideration: [],
            reviewMeeting: [],
            reportReview: [],
            invoiceGenerated: []
          };
        }

        if (item.type === 'Default') {
          categorized.myFollowUps.byDate[followUpDate].default.push(item);
        } else if (item.type === 'Payment') {
          categorized.myFollowUps.byDate[followUpDate].payment.push(item);
        } else if (item.type === 'Posession Date') {
          categorized.myFollowUps.byDate[followUpDate].possession.push(item);
        } else if (item.type === 'ClosedDeal Details') {
          categorized.myFollowUps.byDate[followUpDate].closeddeal.push(item);
        } else if (item.type === 'UnderConsideration Details') {
          categorized.myFollowUps.byDate[followUpDate].underConsideration.push(item);
        } else if (item.type === 'Review Meeting') {
          categorized.myFollowUps.byDate[followUpDate].reviewMeeting.push(item);
        } else if (item.type === 'Report Review') {
          categorized.myFollowUps.byDate[followUpDate].reportReview.push(item);
        } else if (item.type === 'Invoice Generated') {
          categorized.myFollowUps.byDate[followUpDate].invoiceGenerated.push(item);
        }
      } else {
        // For other sales people (keep existing structure)
        const salesId = item.user?.id;
        if (!salesId) return;

        if (!categorized.otherSales[salesId]) {
          categorized.otherSales[salesId] = {
            user: item.user,
            escalated: [],
            absent: []
          };
        }

        if (item.isEscalated) {
          const leadStatus = item?.lead?.status;
          const leadPstatus = item?.lead?.pstatus;
          const stage = item?.stage;
          // A finished deal cannot be chased: Closed Lost also unassigns the lead
          const isDealClosed =
            leadStatus === 'Closed' ||
            leadPstatus === 'Closed Won' ||
            leadPstatus === 'Closed Lost' ||
            stage === 'Deal Closed' ||
            stage === 'Closed Won' ||
            stage === 'Deal Dropped' ||
            stage === 'Deal Cancelled';
          if (!isDealClosed) {
            categorized.otherSales[salesId].escalated.push(item);
          }
        }
        if (item.isAbsent) {
          categorized.otherSales[salesId].absent.push(item);
        }
      }
    });

    // Create display structure
    const displayData = [];

    // Add My Follow Ups section if there are any
    const myFollowUpDates = Object.keys(categorized.myFollowUps.byDate);
    if (myFollowUpDates.length > 0) {
      displayData.push({
        type: 'header',
        title: 'My Follow Up'
      });

      // Sort dates chronologically (convert to Date objects for proper sorting)
      const sortedDates = myFollowUpDates
        .filter((date) => date !== 'No Date')
        .sort((a, b) => {
          const dateA = new Date(a);
          const dateB = new Date(b);
          return dateA - dateB;
        });

      // Add "No Date" items at the end if they exist
      if (categorized.myFollowUps.byDate['No Date']) {
        sortedDates.push('No Date');
      }

      sortedDates.forEach((date) => {
        const dateData = categorized.myFollowUps.byDate[date];

        // Add date header
        displayData.push({
          type: 'dateHeader',
          date: date
        });

        // Add default follow-ups for this date
        if (dateData.default.length > 0) {
          displayData.push({
            type: 'item',
            category: 'default',
            user: { id: userID, name: currentUserName },
            count: dateData.default.length,
            items: dateData.default,
            date: date
          });
        }

        // Add payment follow-ups for this date
        if (dateData.payment.length > 0) {
          displayData.push({
            type: 'item',
            category: 'payment',
            user: { id: userID, name: currentUserName },
            count: dateData.payment.length,
            items: dateData.payment,
            date: date
          });
        }

        // Add possession date follow-ups for this date
        if (dateData.possession.length > 0) {
          displayData.push({
            type: 'item',
            category: 'possession',
            user: { id: userID, name: currentUserName },
            count: dateData.possession.length,
            items: dateData.possession,
            date: date
          });
        }

        // Add closed deal details follow-ups for this date
        if (dateData.closeddeal.length > 0) {
          displayData.push({
            type: 'item',
            category: 'closeddeal',
            user: { id: userID, name: currentUserName },
            count: dateData.closeddeal.length,
            items: dateData.closeddeal,
            date: date
          });
        }

        // Add under consideration details follow-ups for this date
        if (dateData.underConsideration.length > 0) {
          displayData.push({
            type: 'item',
            category: 'underConsideration',
            user: { id: userID, name: currentUserName },
            count: dateData.underConsideration.length,
            items: dateData.underConsideration,
            date: date
          });
        }

        // Add help request follow-ups for this date
        if (dateData.reviewMeeting.length > 0) {
          displayData.push({
            type: 'item',
            category: 'reviewMeeting',
            user: { id: userID, name: currentUserName },
            count: dateData.reviewMeeting.length,
            items: dateData.reviewMeeting,
            date: date
          });
        }

        // Add report review follow-ups for this date
        if (dateData.reportReview.length > 0) {
          displayData.push({
            type: 'item',
            category: 'reportReview',
            user: { id: userID, name: currentUserName },
            count: dateData.reportReview.length,
            items: dateData.reportReview,
            date: date
          });
        }

        // Add invoice generated follow-ups for this date
        if (dateData.invoiceGenerated.length > 0) {
          dateData.invoiceGenerated.forEach((invItem) => {
            displayData.push({
              type: 'item',
              category: 'invoiceGenerated',
              user: { id: userID, name: currentUserName },
              count: 1,
              items: [invItem],
              date: date
            });
          });
        }
      });
    }

    // Add Other Sales section if there are any (keep existing structure)
    const otherSalesEntries = Object.values(categorized.otherSales);
    if (otherSalesEntries.length > 0) {
      displayData.push({
        type: 'header',
        title: 'Other Sales Follow Ups'
      });

      otherSalesEntries.forEach((salesData) => {
        if (salesData.escalated.length > 0) {
          displayData.push({
            type: 'item',
            category: 'escalated',
            user: salesData.user,
            count: salesData.escalated.length,
            items: salesData.escalated
          });
        }

        if (salesData.absent.length > 0) {
          displayData.push({
            type: 'item',
            category: 'absent',
            user: salesData.user,
            count: salesData.absent.length,
            items: salesData.absent
          });
        }
      });
    }

    return displayData;
  };

  const followUpsDisplayData = useMemo(
    () => processFollowUpsData(followUps?.data),
    [followUps?.data, userID]
  );

  // Prefer shell summary counts (real DB totals) over capped preview array length
  const salesPrioritiesTabCount = useMemo(() => {
    const fromSummary = Number(followUps?.meta?.activeCount);
    if (Number.isFinite(fromSummary) && fromSummary >= 0) return fromSummary;
    return (followUpsDisplayData || []).reduce(
      (sum, row) =>
        row.type === 'item' && typeof row.count === 'number' ? sum + row.count : sum,
      0
    );
  }, [followUps?.meta?.activeCount, followUpsDisplayData]);

  const salesApprovalsTabCount = useMemo(
    () => filteredAvailabilityData.reduce((sum, [, items]) => sum + items.length, 0),
    [filteredAvailabilityData]
  );

  const todaysPriorityFollowUpsCount = useMemo(() => {
    const fromSummary = Number(followUps?.meta?.todayCount);
    if (Number.isFinite(fromSummary) && fromSummary >= 0) return fromSummary;
    const today = new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
    return (followUpsDisplayData || []).reduce((sum, row) => {
      if (row.type !== 'item' || typeof row.count !== 'number') return sum;
      return row.date === today ? sum + row.count : sum;
    }, 0);
  }, [followUps?.meta?.todayCount, followUpsDisplayData]);

  const formatRupee = (value) => {
    const amount = Number(value || 0);

    if (!Number.isFinite(amount)) return 'Rs 0';

    const absAmount = Math.abs(amount);
    const formatCompact = (num, suffix) => {
      const compact = Number(num.toFixed(2)).toString();
      return `Rs ${compact} ${suffix}`;
    };

    if (absAmount >= 10000000) return formatCompact(amount / 10000000, 'Cr');
    if (absAmount >= 100000) return formatCompact(amount / 100000, 'L');
    if (absAmount >= 1000) return formatCompact(amount / 1000, 'K');
    if (absAmount >= 100) return formatCompact(amount / 100, 'H');

    return `Rs ${amount.toLocaleString('en-IN')}`;
  };

  const totalSalesValueAmount = Number(salesStatsCards?.data?.totalSalesValue || 0);
  const amountReceived = Number(salesStatsCards?.data?.amountReceived || 0);
  const receivablesDifference = Math.max(totalSalesValueAmount - amountReceived, 0);

  const salesMetricCards = [
    {
      label: 'Total Leads',
      value: salesStatsCards?.data?.totalLeads ?? 0,
      icon: Users,
      iconBgClass: 'bg-[#EAF4F7]',
      iconColorClass: 'text-[#2A565E]',
      valueColorClass: 'text-[#2A565E]',
      borderClass: 'border-[#D7E8EE]',
      showArrow: true,
      onClick: () =>
        navigateWithReturn(
          navigate,
          `/builder/sales/?view=leads&tab=Leads${
            filters.projectID ? `&project_id=${filters.projectID}` : ''
          }`,
          {
            returnTo: buildReturnToFromLocation(location),
            state: {
              secondaryTab: 'Leads',
              ...(filters.projectID ? { projectID: filters.projectID } : {})
            }
          }
        )
    },
    {
      label: 'Total Prelead',
      value: salesStatsCards?.data?.totalPreLead ?? 0,
      icon: UserPlus,
      iconBgClass: 'bg-[#EEF2FF]',
      iconColorClass: 'text-[#4F46E5]',
      valueColorClass: 'text-[#3730A3]',
      borderClass: 'border-[#DDE3F8]'
    },
    {
      label: 'Closed Won',
      value: salesStatsCards?.data?.closedWon ?? 0,
      icon: CircleCheck,
      iconBgClass: 'bg-[#E9F8F1]',
      iconColorClass: 'text-[#0F766E]',
      valueColorClass: 'text-[#0F766E]',
      borderClass: 'border-[#CFEBDD]',
      showArrow: true,
      onClick: () =>
        navigateWithReturn(
          navigate,
          `/builder/sales/deals${filters.projectID ? `?projectID=${filters.projectID}` : ''}`,
          {
            returnTo: buildReturnToFromLocation(location),
            state: { projectID: filters.projectID || null }
          }
        )
    },
    {
      label: 'Closed Lost',
      value: salesStatsCards?.data?.closedLost ?? 0,
      icon: CircleX,
      iconBgClass: 'bg-[#FFF1F2]',
      iconColorClass: 'text-[#BE123C]',
      valueColorClass: 'text-[#BE123C]',
      borderClass: 'border-[#FFD6DD]',
      showArrow: true,
      onClick: () =>
        navigateWithReturn(
          navigate,
          `/builder/sales/closed-lost${filters.projectID ? `?projectID=${filters.projectID}` : ''}`,
          {
            returnTo: buildReturnToFromLocation(location),
            state: { projectID: filters.projectID || null }
          }
        )
    },
    {
      label: 'Nurture pipeline',
      value: leadPipelineStats?.data?.nurtureLead ?? 0,
      icon: PauseCircle,
      iconBgClass: 'bg-[#FFF7ED]',
      iconColorClass: 'text-[#C2410C]',
      valueColorClass: 'text-[#C2410C]',
      borderClass: 'border-[#FED7AA]',
      showArrow: true,
      onClick: () =>
        navigateWithReturn(
          navigate,
          `/builder/sales/total-leads/nurture${filters.projectID ? `?projectID=${filters.projectID}` : ''}`,
          {
            returnTo: buildReturnToFromLocation(location),
            state: { projectID: filters.projectID || null }
          }
        )
    },
    {
      label: 'Future Prospects',
      value: leadPipelineStats?.data?.futureProspectLead ?? 0,
      icon: CalendarClock,
      iconBgClass: 'bg-[#F0FDFA]',
      iconColorClass: 'text-[#0F766E]',
      valueColorClass: 'text-[#0F766E]',
      borderClass: 'border-[#99F6E4]',
      showArrow: true,
      onClick: () =>
        navigateWithReturn(
          navigate,
          `/builder/sales/total-leads/future-prospect${filters.projectID ? `?projectID=${filters.projectID}` : ''}`,
          {
            returnTo: buildReturnToFromLocation(location),
            state: { projectID: filters.projectID || null }
          }
        )
    },
    {
      label: 'Under Consideration',
      value: salesStatsCards?.data?.underConsideration ?? 0,
      icon: Handshake,
      iconBgClass: 'bg-[#F3E8FF]',
      iconColorClass: 'text-[#7E22CE]',
      valueColorClass: 'text-[#7E22CE]',
      borderClass: 'border-[#E9D5FF]',
      showArrow: true,
      onClick: () =>
        navigateWithReturn(
          navigate,
          `/builder/sales/under-consideration${filters.projectID ? `?projectID=${filters.projectID}` : ''}`,
          {
            returnTo: buildReturnToFromLocation(location),
            state: { projectID: filters.projectID || null }
          }
        )
    },
    // {
    //   label: 'Pending Follow Ups',
    //   value: salesPrioritiesTabCount,
    //   icon: Clock3,
    //   iconBgClass: 'bg-[#FFF7ED]',
    //   iconColorClass: 'text-[#C2410C]',
    //   valueColorClass: 'text-[#C2410C]',
    //   borderClass: 'border-[#FED7AA]'
    // },
    {
      label: "Today's Follow Up",
      value: todaysPriorityFollowUpsCount,
      icon: CalendarCheck2,
      iconBgClass: 'bg-[#DCFCE7]',
      iconColorClass: 'text-[#15803D]',
      valueColorClass: 'text-[#166534]',
      borderClass: 'border-[#BBF7D0]'
    },
    {
      label: 'Total Sales Value',
      value: formatRupee(salesStatsCards?.data?.totalSalesValue),
      icon: IndianRupee,
      iconBgClass: 'bg-[#FEF9C3]',
      iconColorClass: 'text-[#A16207]',
      valueColorClass: 'text-[#A16207]',
      borderClass: 'border-[#FEF08A]'
    },
    {
      label: 'Amount Received',
      value: formatRupee(salesStatsCards?.data?.amountReceived),
      icon: Wallet,
      iconBgClass: 'bg-[#DCFCE7]',
      iconColorClass: 'text-[#166534]',
      valueColorClass: 'text-[#166534]',
      borderClass: 'border-[#BBF7D0]'
    },
    {
      label: 'Overdue',
      value: formatRupee(salesStatsCards?.data?.overdue),
      icon: AlertTriangle,
      iconBgClass: 'bg-[#FFF7ED]',
      iconColorClass: 'text-[#C2410C]',
      valueColorClass: 'text-[#C2410C]',
      borderClass: 'border-[#FED7AA]'
    },
    {
      label: 'Receivables',
      value: formatRupee(salesStatsCards?.data?.receivables),
      icon: HandCoins,
      iconBgClass: 'bg-[#F3E8FF]',
      iconColorClass: 'text-[#7E22CE]',
      valueColorClass: 'text-[#7E22CE]',
      borderClass: 'border-[#E9D5FF]',
      showArrow: true,
      onClick: () =>
        navigateWithReturn(
          navigate,
          `/builder/sales/receivables${filters.projectID ? `?projectID=${filters.projectID}` : ''}`,
          {
            returnTo: buildReturnToFromLocation(location),
            state: { projectID: filters.projectID || null }
          }
        )
    },
    {
      label: 'Pending amount',
      value: formatRupee(receivablesDifference),
      icon: Clock3,
      iconBgClass: 'bg-[#FFFBEB]',
      iconColorClass: 'text-[#B45309]',
      valueColorClass: 'text-[#B45309]',
      borderClass: 'border-[#FDE68A]',
      showArrow: true,
      onClick: () =>
        navigateWithReturn(
          navigate,
          `/builder/sales/receivables${filters.projectID ? `?projectID=${filters.projectID}` : ''}`,
          {
            returnTo: buildReturnToFromLocation(location),
            state: { projectID: filters.projectID || null }
          }
        )
    },
    {
      label: 'Inventory',
      value: salesStatsCards?.data?.totalUnitUnsold ?? 0,
      icon: Home,
      iconBgClass: 'bg-[#E0F2FE]',
      iconColorClass: 'text-[#0369A1]',
      valueColorClass: 'text-[#0369A1]',
      borderClass: 'border-[#BAE6FD]',
      showArrow: true,
      onClick: () =>
        navigateWithReturn(
          navigate,
          `/builder/sales/?view=leads&tab=${encodeURIComponent('Unit Inventory')}${filters.projectID ? `&projectID=${filters.projectID}` : ''}`,
          {
            returnTo: buildReturnToFromLocation(location),
            state: { secondaryTab: 'Unit Inventory', projectID: filters.projectID || null }
          }
        )
    }
  ];

  const mapFollowUpDisplayItem = (item, index, keyPrefix = 'row') => {
    if (item.type === 'header') {
      return (
        <p
          key={`${keyPrefix}-header-${index}`}
          className="font-semibold text-sm text-gray-600 mb-2 mt-4 first:mt-0">
          {item.title}
        </p>
      );
    }

    if (item.type === 'dateHeader') {
      return (
        <p key={`${keyPrefix}-date-${index}`} className="font-semibold text-sm text-gray-600 mb-2 mt-4">
          {item.date}
        </p>
      );
    }

    let label = '';
    let action = '';

    switch (item.category) {
      case 'default':
        label = 'Follow Up';
        action = 'Leads';
        break;
      case 'payment':
        label = 'Payment Follow Up';
        action = 'Leads';
        break;
      case 'possession':
        label = 'Possession Date';
        action = 'Leads';
        break;
      case 'closeddeal':
        label = 'Closed Deal Details';
        action = 'Leads';
        break;
      case 'underConsideration':
        label = 'Under Consideration Details';
        action = 'Leads';
        break;
      case 'reviewMeeting':
        label = 'Attend sales review meeting';
        action = 'Reports';
        break;
      case 'reportReview':
        label = 'Report review for sales meeting';
        action = 'Reports';
        break;
      case 'escalated':
        label = 'Escalation';
        action = 'Pending Tasks';
        break;
      case 'absent':
        label = 'Is Absent';
        action = 'Assign Agent';
        break;
      case 'invoiceGenerated':
        label = 'Invoice Generated';
        action = 'Leads';
        break;
      default:
        break;
    }

    if (item.category === 'invoiceGenerated') {
      const detail = item.items[0];
      const dealId = detail?.invoice?.deal_id;
      const isDownloading = downloadingInvoiceId === dealId;

      return (
        <div
          key={`${keyPrefix}-${item?.user?.id}-${item?.category}-${index}`}
          className="flex flex-col gap-2 border border-gray-200 rounded-lg p-3 cursor-pointer hover:bg-gray-50 mb-2 transition-colors"
          onClick={() => {
            if (item.user?.id === Number(Cookies.get('userId'))) {
              toast('invoiceGenerated');
            }
          }}>
          <div className="flex justify-between items-start">
            <div className="flex items-center gap-2">
              <AvatarCircle name={item?.user?.name} />
              <span className="text-sm">
                <strong>{item?.user?.name}</strong> | {label}
              </span>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (dealId && !isDownloading) {
                  handleDownloadInvoice(dealId);
                }
              }}
              disabled={isDownloading}
              className="px-3 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors">
              {isDownloading ? 'Downloading...' : 'Download'}
            </button>
          </div>
          <div className="pl-8 text-xs text-gray-600 space-y-1">
            <p>
              Customer: <strong>{detail?.lead?.user?.name || 'N/A'}</strong>
            </p>
            <p>
              Project: {detail?.project?.name || 'N/A'} | Amount: ₹
              {detail?.invoice?.amount || 0}
            </p>
            {(detail?.unit?.block?.name ||
              detail?.unit?.name ||
              detail?.invoice?.closedDeal?.unit?.unitName ||
              detail?.invoice?.closedDeal?.unit?.block?.name) && (
              <p>
                {(detail?.unit?.block?.name || detail?.invoice?.closedDeal?.unit?.block?.name) &&
                  `Block: ${
                    detail?.unit?.block?.name || detail?.invoice?.closedDeal?.unit?.block?.name
                  }`}
                {(detail?.unit?.block?.name || detail?.invoice?.closedDeal?.unit?.block?.name) &&
                  (detail?.unit?.name || detail?.invoice?.closedDeal?.unit?.unitName) &&
                  ' | '}
                {(detail?.unit?.name || detail?.invoice?.closedDeal?.unit?.unitName) &&
                  `Unit Name : ${
                    detail?.unit?.name || detail?.invoice?.closedDeal?.unit?.unitName
                  }`}
              </p>
            )}
          </div>
        </div>
      );
    }

    const avatarVariant =
      item.category === 'escalated'
        ? 'escalation'
        : item.category === 'absent'
          ? 'absent'
          : 'priority';

    return (
      <div
        key={`${keyPrefix}-${item?.user?.id}-${item?.category}-${index}`}
        className="flex items-start gap-2 border border-gray-200 rounded-lg p-2 cursor-pointer hover:bg-gray-50 mb-2"
        onClick={() => {
          const isMine = item?.user?.id === Number(Cookies.get('userId'));

          mixpanel.track('Priority Item Clicked', {
            user_id: userID,
            priority_type: item.category,
            target_user_id: item?.user?.id,
            target_user_name: item?.user?.name,
            is_mine: isMine,
            count: item?.count,
            project_id: filters.projectID,
            timestamp: new Date().toISOString()
          });

          if (isMine) {
            if (item.category === 'invoiceGenerated') {
              toast('invoiceGenerated');
              return;
            }
            let filterType = 'Default';
            if (item.category === 'payment') {
              filterType = 'Payment';
            } else if (item.category === 'possession') {
              filterType = 'Possession Date';
            } else if (item.category === 'closeddeal') {
              filterType = 'ClosedDeal Details';
            } else if (item.category === 'underConsideration') {
              filterType = 'UnderConsideration Details';
            } else if (item.category === 'reviewMeeting') {
              navigateWithReturn(
              navigate,
              `/builder/sales/reports${filters.projectID ? `?projectID=${filters.projectID}` : ''}${filters.month ? `${filters.projectID ? '&' : '?'}month=${filters.month}` : ''}`,
              {
                returnTo: buildReturnToFromLocation(location),
                state: {
                  mode: 'viewOnly',
                  projectID: filters.projectID,
                  month: filters.month
                }
              }
            );
              return;
            } else if (item.category === 'reportReview') {
              const followUpId = item?.items?.[0]?.id || null;
              navigateWithReturn(
                navigate,
                `/builder/sales/reports${filters.projectID ? `?projectID=${filters.projectID}` : ''}`,
                {
                  returnTo: buildReturnToFromLocation(location),
                  state: {
                    mode: 'dashboard',
                    projectID: filters.projectID,
                    month: filters.month,
                    priorityId: followUpId,
                    isFollowUpPriority: true
                  }
                }
              );
              return;
            }

            goToPriorityLeads({
              salesID: item?.user?.id,
              salesName: item?.user?.name,
              filterType: filterType,
              followUpDate: item.date === 'No Date' ? null : item.date,
              followUpIds: (item?.items || []).map((f) => f.id).filter(Boolean),
              projectID: filters.projectID
            });
          } else if (item.category === 'escalated') {
            goToPriorityLeads({
              salesID: item?.user?.id,
              salesName: item?.user?.name,
              filterType: 'Escalated',
              followUpIds: (item?.items || []).map((f) => f.id).filter(Boolean),
              projectID: filters.projectID
            });
          } else if (item.category === 'absent') {
            goToPriorityLeads({
              salesID: item?.user?.id,
              salesName: item?.user?.name,
              filterType: 'Absent',
              followUpIds: (item?.items || []).map((f) => f.id).filter(Boolean),
              projectID: filters.projectID
            });
          }
        }}>
        <span className="text-sm flex items-center gap-2">
          <AvatarCircle name={item?.user?.name} variant={avatarVariant} />
          <strong>{item?.user?.name}</strong> | {label} | {item?.count}{' '}
          {item?.count === 1 ? 'follow-up' : 'follow-ups'}
        </span>
      </div>
    );
  };

  const handleAcceptLeave = () => {
    const payload = {
      status: true
    };

    // Track leave acceptance
    mixpanel.track('Leave Request Accepted', {
      user_id: userID,
      activity_id: selectedActivity,
      timestamp: new Date().toISOString()
    });

    handleAcceptRejectTarget({ payload, type: 'Accepted' });
  };

  const handleRejectLeave = () => {
    const payload = {
      status: false
    };

    // Track leave rejection
    mixpanel.track('Leave Request Rejected', {
      user_id: userID,
      activity_id: selectedActivity,
      timestamp: new Date().toISOString()
    });

    handleAcceptRejectTarget({ payload, type: 'Rejected' });
  };

  const { mutate: handleDownloadInvoice } = useMutation({
    mutationFn: async (dealId) => {
      setDownloadingInvoiceId(dealId);
      return await downloadInvoice(dealId);
    },
    onSuccess: (data, dealId) => {
      try {
        if (isEmptyPdfPayload(data)) {
          toast.error('Error: File data is empty or invalid.');
          return;
        }
        triggerPdfDownload(data, `invoice_${dealId}.pdf`);
        toast.success('Invoice downloaded successfully');

        mixpanel.track('Invoice Downloaded from Dashboard', {
          user_id: userID,
          deal_id: dealId,
          timestamp: new Date().toISOString()
        });
      } catch (err) {
        console.error('Error creating download:', err);
        toast.error('Error downloading invoice');
      } finally {
        setDownloadingInvoiceId(null);
      }
    },
    onError: (err, dealId) => {
      console.error('Error downloading invoice:', err);
      const message = axiosErrorMessage(err, 'Error downloading invoice');
      toast.error(message);
      setDownloadingInvoiceId(null);

      mixpanel.track('Invoice Download Failed', {
        user_id: userID,
        deal_id: dealId,
        error_message: message,
        timestamp: new Date().toISOString()
      });
    }
  });

  const { mutate: handleAcceptRejectTarget } = useMutation({
    mutationFn: ({ payload }) => acceptRejectLeave(payload, selectedActivity),
    onMutate: () => {
      setIsSubmitting(true); // Disable the button when mutation starts

      mixpanel.track('Leave Request Process Started', {
        user_id: userID,
        activity_id: selectedActivity,
        timestamp: new Date().toISOString()
      });
    },
    onSuccess: async (result) => {
      setIsSubmitting(false);

      // Check if result is an error object (API now returns errors instead of throwing)
      if (
        result?.response?.status ||
        result?.message?.includes('Error') ||
        result?.name === 'AxiosError'
      ) {
        // Handle as error
        console.error('Error accepting/rejecting leaves', result);
        toast.error(
          result?.response?.data?.message || result?.message || 'Error accepting/rejecting leaves'
        );

        mixpanel.track('Leave Request Process Failed', {
          user_id: userID,
          activity_id: selectedActivity,
          error_message: result?.response?.data?.message || result?.message || 'Unknown error',
          timestamp: new Date().toISOString()
        });
        return;
      }

      // Handle as success
      const type = result?.type || 'processed';
      toast.success(`Leave ${type} Successfully`);
      setShowConfirmationModal(false);

      mixpanel.track('Leave Request Process Success', {
        user_id: userID,
        activity_id: selectedActivity,
        action_type: type,
        timestamp: new Date().toISOString()
      });

      await refetAvailability();
      await refetchFollowUps();
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error('Error accepting/rejecting leaves', err);
      toast.error(
        err?.response?.data?.message || err?.message || 'Error accepting/rejecting leaves'
      );

      mixpanel.track('Leave Request Process Failed', {
        user_id: userID,
        activity_id: selectedActivity,
        error_message: err?.response?.data?.message || err?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  });

  const content = (
    <>
      <Loader
        isVisible={
          isSubmitting ||
          isFiltersLoading ||
          isSalesStatsLoading
        }
      />
      {showConfirmationModal && (
        <Modal
          isDirty
          confirmOnClose
          width={45}
          name="Confirmation"
          onClose={() => {
            // Track modal close
            mixpanel.track('Leave Confirmation Modal Closed', {
              user_id: userID,
              activity_id: selectedActivity,
              timestamp: new Date().toISOString()
            });

            setShowConfirmationModal(false);
            setSelectedActivity();
          }}>
          <div className="flex flex-col justify-start items-center z-10">
            <h1 className=" text-center text-[1.4rem] font-medium text-primary">
              Are you sure you want to Accept this leave?
            </h1>
            <div className="w-full flex justify-end items-center space-x-4 mt-8">
              <Button type="reset" onClick={() => handleRejectLeave()} displayType="secondary">
                Reject
              </Button>

              <Button
                disabled={isSubmitting}
                type="button"
                onClick={(e) => {
                  handleAcceptLeave();
                }}
                displayType="primary-red">
                Accept
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {(!skipHeader || !skipFilters) && (
        <div
          className={`fixed top-0 z-10 transition-all duration-300 bg-white ${collapsed ? 'left-[76px] w-[calc(100%-76px)]' : 'left-64 w-[calc(100%-16rem)]'
            }`}>
          {!skipHeader && (
            <Header
              data={{ name: 'Dashboard' }}
              backArrow={showHomeBack}
              onBack={showHomeBack ? onBack : undefined}
            />
          )}
          {!skipFilters && (
            <DashboardFilters
              onFiltersChange={handleFiltersChange}
              onLoadingChange={handleFiltersLoadingChange}
              top={skipHeader ? '4rem' : '4rem'}
            />
          )}
        </div>
      )}

      {/* Body Content */}
      <div className={`transition-all duration-300 ease-in-out ${collapsed ? 'ml-[1vw]' : ''}`}>
        <div
          className={`flex justify-flex-start items-start relative p-2 gap-7 pl-5 ${skipHeader && skipFilters ? 'pt-[7rem]' : 'pt-[8.8rem]'
            }`}>
          {/* Graphs + Metrics Section */}
          <div
            className={`flex flex-col gap-5 transition-all duration-300 ease-in-out ${collapsed ? 'w-[63%]' : 'w-[59%]'
              }`}>
            <div className="grid grid-cols-1 gap-3 pt-4 sm:grid-cols-2 lg:grid-cols-3">
              {salesMetricCards.map((metric) => {
                const Icon = metric.icon;
                return (
                <MetricCard
                  key={metric.label}
                  title={metric.label}
                  value={metric.value}
                  icon={<Icon size={18} />}
                  widthClass={metric.widthClass}
                  onClick={metric.onClick}
                  showArrow={metric.showArrow}
                  iconBgClass={metric.iconBgClass}
                  iconColorClass={metric.iconColorClass}
                  valueColorClass={metric.valueColorClass}
                  borderClass={metric.borderClass}
                  titleClassName="text-[10px] sm:text-[11px]"
                  valueClassName="text-[1.55rem] sm:text-[1.7rem]"
                  className="px-4 py-3"
                />
                );
              })}
            </div>
            <AiPulsePanel projectId={filters.projectID} defaultVertical="sales" />
            <SalesGraphs projectID={filters.projectID} month={filters.month} />
          </div>

          {/* Action center — same pattern as management dashboard */}
          <div
            className={`flex h-[75vh] w-[31%] min-w-0 flex-col overflow-hidden bg-gray-100 shadow fixed right-4 ${
              skipHeader && skipFilters ? 'top-[14rem]' : 'top-[10.4rem]'
            }`}>
            <div className="flex h-full min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden rounded-lg bg-white p-4 shadow-lg">
              <div className="shrink-0">
                <h3 className="mb-1 text-lg font-semibold text-[#1E4A53]">Action Center</h3>
                <p className="mb-4 text-xs text-gray-500">
                  Follow-ups, leave approvals, and sales activity in one place.
                </p>
                <div
                  className={`mb-4 grid w-full min-w-0 gap-1 rounded-xl bg-gray-100 p-1 ${
                    designation === 'Low' ? 'grid-cols-2' : 'grid-cols-3'
                  }`}>
                  <button
                    type="button"
                    onClick={() => setSalesActionCenterTab('priorities')}
                    className={`min-w-0 rounded-lg px-1 py-2 text-center text-xs font-semibold leading-tight whitespace-nowrap sm:text-sm ${
                      salesActionCenterTab === 'priorities'
                        ? 'bg-white text-[#0A4A5C] shadow'
                        : 'text-gray-600 hover:bg-white/70'
                    }`}>
                    Priorities ({salesPrioritiesTabCount})
                  </button>
                  {designation !== 'Low' && (
                    <button
                      type="button"
                      onClick={() => setSalesActionCenterTab('approvals')}
                      className={`min-w-0 rounded-lg px-1 py-2 text-center text-xs font-semibold leading-tight whitespace-nowrap sm:text-sm ${
                        salesActionCenterTab === 'approvals'
                          ? 'bg-white text-[#0A4A5C] shadow'
                          : 'text-gray-600 hover:bg-white/70'
                      }`}>
                      Approvals ({salesApprovalsTabCount})
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setSalesActionCenterTab('activity')}
                    className={`min-w-0 rounded-lg px-1 py-2 text-center text-xs font-semibold leading-tight whitespace-nowrap sm:text-sm ${
                      salesActionCenterTab === 'activity'
                        ? 'bg-white text-[#0A4A5C] shadow'
                        : 'text-gray-600 hover:bg-white/70'
                    }`}>
                    Activity
                  </button>
                </div>
              </div>

              <div className="flex min-h-0 flex-1 flex-col overflow-y-auto pr-1">
                {salesActionCenterTab === 'priorities' && (
                  <div className="mb-6 p-1">
                    {followUps?.data && followUpsDisplayData.length > 0 ? (
                      followUpsDisplayData.map((item, index) =>
                        mapFollowUpDisplayItem(item, index, 'p')
                      )
                    ) : (
                      <div className="px-3 py-8 text-center">
                        <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-600">
                          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                            />
                          </svg>
                        </div>
                        <p className="text-sm font-medium text-gray-700">No follow-ups pending</p>
                        <p className="mt-1 text-xs text-gray-500">
                          Nothing requires your action for this project right now.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {salesActionCenterTab === 'approvals' && designation !== 'Low' && (
                  <div className="mb-6 p-1">
                    {filteredAvailabilityData?.length > 0 ? (
                      filteredAvailabilityData.map(([date, items]) => (
                        <div key={date} className="mb-4">
                          <p className="mb-2 text-sm font-semibold text-gray-600">{date}</p>
                          <div className="space-y-2">
                            {items.map((item) => (
                              <div
                                className="flex cursor-pointer items-start gap-2 rounded-lg border border-gray-200 p-2 hover:bg-gray-50"
                                key={item.id}
                                onClick={() => {
                                  mixpanel.track('Approval Item Clicked', {
                                    user_id: userID,
                                    activity_id: item.id,
                                    target_user_id: item?.user?.id,
                                    target_user_name: item?.user?.name,
                                    date: formatDate(item.date),
                                    project_id: filters.projectID,
                                    timestamp: new Date().toISOString()
                                  });

                                  setShowConfirmationModal(true);
                                  setSelectedActivity(item.id);
                                }}>
                                <span className="flex items-center gap-2 text-sm">
                                  <AvatarCircle name={item?.user?.name} variant="approval" />
                                  <strong>{item?.user?.name}</strong> | Leave | Requesting for leave
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="px-3 py-8 text-center">
                        <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-purple-100 text-purple-600">
                          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        </div>
                        <p className="text-sm font-medium text-gray-700">No leave approvals pending</p>
                        <p className="mt-1 text-xs text-gray-500">
                          This tab lists leave requests only. Deal approvals are under Management.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {salesActionCenterTab === 'activity' && (
                  <div className="mb-6 flex min-h-0 flex-col gap-3 p-1">
                    <div className="flex shrink-0 flex-wrap items-center gap-2">
                      <select
                        value={activityDatePreset}
                        onChange={(e) => setActivityDatePreset(e.target.value)}
                        aria-label="Date"
                        className="min-w-0 flex-1 rounded-md border border-gray-200 bg-white px-2 py-1.5 text-xs text-gray-700">
                        {ACTIVITY_DATE_PRESETS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      {designation !== 'Low' && (
                        <select
                          value={activitySalesUserId}
                          onChange={(e) => setActivitySalesUserId(e.target.value)}
                          aria-label="Sales users"
                          className="min-w-0 flex-1 rounded-md border border-gray-200 bg-white px-2 py-1.5 text-xs text-gray-700">
                          <option value="">All users</option>
                          {activityFilterOptions.map((u) => (
                            <option key={u.userId} value={u.userId}>
                              {u.name}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>

                    {activityDatePreset === 'custom' && (
                      <div className="flex shrink-0 flex-wrap items-center gap-2">
                        <input
                          type="date"
                          value={activityCustomFrom}
                          onChange={(e) => setActivityCustomFrom(e.target.value)}
                          className="min-w-0 flex-1 rounded-md border border-gray-200 bg-white px-2 py-1.5 text-xs text-gray-700"
                        />
                        <span className="text-[11px] text-gray-400">to</span>
                        <input
                          type="date"
                          value={activityCustomTo}
                          onChange={(e) => setActivityCustomTo(e.target.value)}
                          className="min-w-0 flex-1 rounded-md border border-gray-200 bg-white px-2 py-1.5 text-xs text-gray-700"
                        />
                      </div>
                    )}

                    <div className="flex shrink-0 items-center justify-between gap-2">
                      <p className="text-[11px] text-gray-500">
                        {activityMetricsData?.periodLabel || 'Activity'}
                        {activityMetricsData?.fromDate
                          ? ` · ${activityMetricsData.fromDate}${
                              activityMetricsData.toDate &&
                              activityMetricsData.toDate !==
                                activityMetricsData.fromDate
                                ? ` – ${activityMetricsData.toDate}`
                                : ''
                            }`
                          : ''}
                        {activityMetricsData?.priorFromDate
                          ? ` · vs ${activityMetricsData.priorFromDate}${
                              activityMetricsData.priorToDate &&
                              activityMetricsData.priorToDate !==
                                activityMetricsData.priorFromDate
                                ? ` – ${activityMetricsData.priorToDate}`
                                : ''
                            }`
                          : ''}
                        {activitySyncedLabel
                          ? ` · Updated ${activitySyncedLabel}`
                          : ''}
                        <span className="text-gray-400"> · Auto every 5 min</span>
                      </p>
                      {isFetchingActivityMetrics ? (
                        <Loader2
                          className="h-3 w-3 shrink-0 animate-spin text-gray-400"
                          aria-label="Refreshing"
                        />
                      ) : null}
                    </div>

                    <SalesUserActivityMetrics
                      metrics={activityMetrics}
                      isLoading={isLoadingActivityMetrics}
                      projectId={filters.projectID}
                      salesUserId={
                        designation === 'Low' ? String(userID) : activitySalesUserId
                      }
                      preset={activityDateParams.preset}
                      from={activityDateParams.from || ''}
                      to={activityDateParams.to || ''}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  // Wrap in Layout only if not skipped
  if (skipLayout) {
    return content;
  }

  return <Layout>{content}</Layout>;
};

export default SalesDashboard;
